<script lang="ts" setup>
  import type { Maybe } from '@/types'

  import { computed, nextTick, ref, watch } from 'vue'
  import { syncRef, useVModel } from '@vueuse/core'

  import { useIMask } from 'vue-imask'

  import { useFieldHandlers } from '@/composables/useFieldHandlers'
  import { isNotEmpty } from '@/helpers'

  const props = withDefaults(
    defineProps<{
      name?: string
      title?: Maybe<string>
      modelValue?: Maybe<string | number>
      maxLength?: number | string
      hideMaxLength?: boolean
      link?: boolean
      navigateEvent?: boolean
      type?:
        | 'number'
        | 'text'
        | 'tel'
        | 'email'
        | 'password'
        | 'url'
        | 'date'
        | 'datetime-local'
      center?: boolean
      disabled?: boolean

      staticPlaceholder?: boolean
      staticPlaceholderText?: string

      dynamicPlaceholder?: boolean
      dynamicPlaceholderClasses?: string
      outlined?: boolean
      inputClasses?: string

      errors?: (string | ((value: any) => string | true))[]
      errorPosition?: 'left' | 'right'
      errorAbsolute?: boolean
      lazyValidate?: boolean
      manualValidation?: boolean

      modelMaskedValue?: string
      mask?: string
      maskEager?: boolean | 'remove' | 'append'

      autocomplete?: string

      prependIconWrapperClasses?: string
      appendIconWrapperClasses?: string

      prompt?: string
      promptClasses?: string
      promptAbsolute?: boolean

      ariaLabelledBy?: string
    }>(),
    {
      name: '',
      title: null,
      modelValue: null,
      link: false,
      navigateEvent: false,
      maxLength: undefined,
      hideMaxLength: false,
      type: 'text',
      center: false,
      disabled: false,

      staticPlaceholder: false,
      staticPlaceholderText: undefined,

      dynamicPlaceholder: true,
      dynamicPlaceholderClasses: undefined,
      outlined: false,
      inputClasses: '',

      errors: () => [],
      errorPosition: 'right',
      errorAbsolute: false,
      lazyValidate: true,
      manualValidation: false,

      modelMaskedValue: '',
      mask: undefined,
      maskEager: undefined,

      autocomplete: 'off',
      prependIconWrapperClasses: '',
      appendIconWrapperClasses: '',

      prompt: undefined,
      promptClasses: '',
      promptAbsolute: false,

      ariaLabelledBy: undefined,
    }
  )
  const emit = defineEmits<{
    (e: 'update:modelValue', value: any): void
    (e: 'update:modelMaskedValue', value: any): void
    (e: 'navigate'): void
  }>()

  const value = useVModel(props, 'modelValue', emit)

  const { wrapperRef, inputRef, placeholderRef, focus, blur, navigate } =
    useFieldHandlers(props)

  const counter = computed(() => {
    if (Number.isInteger(props.modelValue)) return 0

    const length = (props.modelValue as string)?.length ?? 0

    return `${length}/${props.maxLength ?? ''}`
  })

  const placeholderText = computed(
    () => props.staticPlaceholderText ?? props.title ?? ''
  )
  const hasBothPlaceholders = computed(
    () =>
      props.staticPlaceholder &&
      props.staticPlaceholderText &&
      props.dynamicPlaceholder
  )

  function focusOutHandler() {
    if (hasBothPlaceholders.value) return

    blur()
  }
  /**
   * Mask
   * Можно использовать либо маску, либо валидацию с сообщениями об ошибках,
   * одновременно нельзя, т.к. инстанс маски конфликтует с v-model
   * и ререндерингом в некоторых случаях
   * */
  const maskedValue = useVModel(props, 'modelMaskedValue', emit)
  const inputKey = ref(0)

  if (props.mask) {
    const { el, masked } = useIMask({
      mask: props.mask,
      eager: props.maskEager,
    })
    syncRef(inputRef, el, {
      deep: true,
      immediate: true,
      transform: {
        ltr: (left) => left as any,
        rtl: (right) => right as Maybe<HTMLInputElement>,
      },
    })
    syncRef(masked, maskedValue, { immediate: true, direction: 'ltr' })
    watch(
      () => value.value,
      async () => {
        const isFirefox = navigator.userAgent.toLowerCase().includes('firefox')
        if (!isFirefox) return

        inputKey.value++
        await nextTick()
        inputRef.value?.focus()
      }
    )
  }
  /**
   * Validation & errors
   * */
  const wasDirtyOnce = ref(false)
  watch(
    () => value.value,
    () => {
      if (props.manualValidation) {
        if (wasDirtyOnce.value) resetLazyValidation()

        return
      }

      wasDirtyOnce.value = true
    }
  )

  const validationErrorsTexts = computed(() => {
    const fns = props.errors.filter(
      (result) => typeof result === 'function'
    ) as ((value: any) => string | true)[]

    return fns
      .map((maybeFn) => maybeFn(value.value))
      .filter((result) => typeof result === 'string')
  })

  const remoteErrorsTexts = computed(() =>
    props.errors.filter((result) => typeof result === 'string')
  )
  const errorsTexts = computed(() => [
    ...validationErrorsTexts.value,
    ...remoteErrorsTexts.value,
  ])

  const hideErrors = computed(() => {
    return (
      (!props.lazyValidate && !!validationErrorsTexts.value.length) ||
      (props.lazyValidate &&
        !wasDirtyOnce.value &&
        !!validationErrorsTexts.value.length)
    )
  })

  const valid = computed(() => {
    return (
      /**
       * Если проверять на ошибки надо сразу
       * */
      (!props.lazyValidate && !validationErrorsTexts.value.length) ||
      /**
       * Валидно, если еще ничего не вводили (т.к. ленивая валидация)
       * */
      (props.lazyValidate && !wasDirtyOnce.value) ||
      /**
       * Валидно, если вводили и нет ошибок
       * */
      (props.lazyValidate &&
        wasDirtyOnce.value &&
        !validationErrorsTexts.value.length)
    )
  })

  async function validate() {
    wasDirtyOnce.value = true

    await nextTick()

    return valid.value
  }

  function resetLazyValidation() {
    wasDirtyOnce.value = false
  }

  function inputFocus() {
    inputRef.value?.focus()
  }

  defineExpose({ valid, validate, resetLazyValidation, inputFocus })
</script>

<template>
  <div
    ref="wrapperRef"
    :class="
      isNotEmpty(value) ||
      hasBothPlaceholders ||
      props.type === 'date' ||
      props.type === 'datetime-local'
        ? 'active'
        : ''
    "
    class="input-wrapper pt-2"
  >
    <!-- Wrapper for placeholder -->
    <div class="relative">
      <div
        v-if="$slots['prepend-icon']"
        class="absolute"
        :class="[
          props.outlined ? 'left-3 bottom-[11px]' : 'left-0 bottom-[14px]',
          { 'opacity-40': props.disabled },
          props.prependIconWrapperClasses,
        ]"
      >
        <slot name="prepend-icon" />
      </div>

      <div
        v-if="$slots['append-icon']"
        class="absolute"
        :class="[
          props.outlined ? 'right-3 bottom-[11px]' : 'right-0 bottom-[14px]',
          { 'opacity-40': props.disabled },
          props.appendIconWrapperClasses,
        ]"
      >
        <slot
          name="append-icon"
          :error="
            (!hideErrors && !!validationErrorsTexts.length) ||
            !!remoteErrorsTexts.length
          "
        />
      </div>

      <!-- Input -->
      <input
        ref="inputRef"
        :key="inputKey"
        v-model="value"
        :aria-labelledby="props.ariaLabelledBy"
        class="inline-block w-full h-11 ring-0 outline-none disabled:bg-white disabled:opacity-40"
        :class="[
          { 'text-center pl-3': props.center },
          { 'static-placeholder': props.staticPlaceholder || props.outlined },
          { 'border-b border-mblack': !props.outlined },
          { 'border border-mbrd outlined !h-10 pr-3': props.outlined },
          { 'pr-[32px]': props.outlined && $slots['append-icon'] },
          { 'prepend-icon': $slots['prepend-icon'] },
          { 'pl-3': props.outlined && !$slots['prepend-icon'] },
          props.inputClasses,
        ]"
        :type="props.type"
        :name="props.name"
        :maxlength="props.maxLength"
        :disabled="props.disabled"
        :placeholder="
          props.staticPlaceholder || props.outlined ? placeholderText : ''
        "
        :autocomplete="props.autocomplete"
        @focusin="focus"
        @focusout="focusOutHandler"
      />

      <!-- Dynamic placeholder -->
      <div
        v-if="props.title && props.dynamicPlaceholder && !props.outlined"
        ref="placeholderRef"
        class="title absolute top-0 left-0 translate-y-2.5 transition-all duration-100 pointer-events-none"
        :class="[
          props.dynamicPlaceholderClasses,
          { 'opacity-40': props.disabled },
        ]"
      >
        {{ props.title }}
      </div>
    </div>

    <div class="flex items-center relative">
      <!-- Prompt -->
      <div
        v-if="props.prompt"
        class="select-none mt-1 text-mgray text-xs mr-2"
        :class="[
          props.promptClasses,
          props.promptAbsolute ? 'absolute top-0 left-0' : '',
        ]"
      >
        {{ props.prompt }}
      </div>

      <!-- Length counter -->
      <div
        v-if="props.maxLength && !props.hideMaxLength"
        class="counter select-none mt-1 text-mgray text-xs"
      >
        {{ counter }}
      </div>

      <!-- Link -->
      <div
        v-if="props.link"
        class="link cursor-pointer mt-1 text-xs"
        @click.prevent="props.navigateEvent ? emit('navigate') : navigate()"
      >
        <svg
          class="inline-block align-middle mr-2"
          width="16"
          height="17"
          viewBox="0 0 16 17"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M7.99219 8.50784L12.9999 3.5M12.9999 3.5H10.0046M12.9999 3.5V6.51085M7.00152 4.5H4.33124C3.59602 4.5 3 5.09603 3 5.83126V12.1687C3 12.904 3.59602 13.5 4.33124 13.5H10.6546C11.3898 13.5 11.9858 12.904 11.9858 12.1687V9.49062"
            stroke="#7A7A7A"
            stroke-width="1.2"
            stroke-linecap="square"
          />
        </svg>

        <span class="inline-block align-middle text-mgray">
          Перейти по ссылке
        </span>
      </div>

      <div
        v-if="
          (!hideErrors && validationErrorsTexts.length) ||
          remoteErrorsTexts.length
        "
        class="mt-1 text-mred text-xs"
        :class="[
          props.errorPosition === 'right' ? 'ml-auto' : '',
          props.errorAbsolute ? 'absolute top-0' : '',
          props.errorAbsolute
            ? props.errorPosition === 'right'
              ? 'right-0'
              : 'left-0'
            : '',
        ]"
      >
        {{ errorsTexts[0] }}
      </div>
    </div>
  </div>
</template>

<style scoped>
  .input-wrapper.active .title {
    font-size: theme('fontSize.xs') !important;
    transform: translateY(-50%);
  }

  .input-wrapper.active input.outlined {
    @apply border-b-mblack;
  }

  .static-placeholder.prepend-icon {
    @apply pl-[26px];
  }

  .outlined.prepend-icon {
    @apply pl-[36px];
  }

  .outlined.append-icon {
    @apply pr-[36px];
  }

  input::placeholder {
    color: theme('colors.mgray2');
  }
</style>
